import { useEffect, useState, useCallback } from "react";
import {
  Receipt, Plus, Trash, LinkSimple, Bank, DownloadSimple, Wallet, ArrowsLeftRight, Copy,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, StatusBadge, StatCard } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useCurrency } from "@/context/CurrencyContext";
import { COUNTRIES } from "@/lib/countries";

const SYMBOLS = { usd: "$", eur: "€", gbp: "£", inr: "₹", aed: "AED ", cad: "C$", aud: "A$", jpy: "¥" };
const money = (n, cur) =>
  `${SYMBOLS[(cur || "usd").toLowerCase()] || "$"}${Number(n || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })}`;

const BLANK_REQUEST = { description: "", amount: 0, currency: "usd", payer_name: "", payer_email: "", due_date: "", note: "" };
const BLANK_CHANNEL = {
  label: "", method: "bank", currency: "usd", account_name: "", account_number: "",
  bank_name: "", swift_iban: "", upi_id: "", country: "", is_default: false,
};

export default function Payments() {
  const { user } = useAuth();
  const { currencies } = useCurrency();
  const [view, setView] = useState("activity");
  const [direction, setDirection] = useState("all");
  const [ledger, setLedger] = useState({ rows: [], totals: {} });
  const [requests, setRequests] = useState([]);
  const [channels, setChannels] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [summary, setSummary] = useState([]);
  const [reqOpen, setReqOpen] = useState(false);
  const [chanOpen, setChanOpen] = useState(false);
  const [payoutOpen, setPayoutOpen] = useState(false);
  const [reqForm, setReqForm] = useState({ ...BLANK_REQUEST });
  const [chanForm, setChanForm] = useState({ ...BLANK_CHANNEL });
  const [payoutForm, setPayoutForm] = useState(null);

  const isAdmin = user?.role === "admin";
  const currencyOptions = Object.keys(currencies || { usd: 1 });

  const load = useCallback(async () => {
    const calls = [
      api.get("/payments/ledger", { params: { direction } }),
      api.get("/payment-requests"),
      api.get("/payout-channels"),
      api.get("/payouts"),
    ];
    if (isAdmin) calls.push(api.get("/payouts/summary"));
    const [l, r, c, p, s] = await Promise.all(calls);
    setLedger(l.data);
    setRequests(r.data);
    setChannels(c.data);
    setPayouts(p.data);
    if (s) setSummary(s.data.rows);
  }, [direction, isAdmin]);

  useEffect(() => {
    load();
  }, [load]);

  const createRequest = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/payment-requests", { ...reqForm, amount: Number(reqForm.amount) });
      setReqOpen(false);
      setReqForm({ ...BLANK_REQUEST });
      load();
      copyLink(data.token, true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const copyLink = (token, created) => {
    const link = `${window.location.origin}/pay/${token}`;
    navigator.clipboard?.writeText(link)?.catch(() => {});
    toast.success(created ? "Payment link created and copied" : "Payment link copied");
  };

  const cancelRequest = async (id) => {
    try {
      await api.patch(`/payment-requests/${id}/cancel`);
      toast.success("Request cancelled");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const saveChannel = async (e) => {
    e.preventDefault();
    try {
      await api.post("/payout-channels", chanForm);
      toast.success("Payout channel saved");
      setChanOpen(false);
      setChanForm({ ...BLANK_CHANNEL });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const removeChannel = async (id) => {
    try {
      await api.delete(`/payout-channels/${id}`);
      toast.success("Payout channel removed");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const recordPayout = async (e) => {
    e.preventDefault();
    try {
      await api.post("/payouts", {
        payee_id: payoutForm.payee_id,
        amount: Number(payoutForm.amount),
        currency: payoutForm.currency,
        method: payoutForm.method,
        reference: payoutForm.reference,
        note: payoutForm.note,
      });
      toast.success("Payout recorded");
      setPayoutOpen(false);
      setPayoutForm(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const downloadReceipt = async (row) => {
    try {
      const res = await api.get(`/payments/receipt/${row.id}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `receipt-${row.reference || row.id}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Receipt downloaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const t = ledger.totals || {};

  return (
    <div data-testid="payments-page">
      <PageHeader
        overline="Money"
        title="Payments"
        description="Everything paid and received across purchase orders, shipments and marketplace — plus payment links anyone can pay, payout channels for local carriers in any currency, and downloadable receipts."
        testid="payments-header"
        action={
          <div className="flex flex-wrap gap-2">
            <Button data-testid="new-request-button" onClick={() => { setReqForm({ ...BLANK_REQUEST }); setReqOpen(true); }} className="w-full sm:w-auto">
              <Plus size={16} className="mr-1.5" /> Request payment
            </Button>
            <Button data-testid="new-channel-button" variant="outline" onClick={() => { setChanForm({ ...BLANK_CHANNEL }); setChanOpen(true); }} className="w-full sm:w-auto">
              <Bank size={16} className="mr-1.5" /> Payout channel
            </Button>
          </div>
        }
      />

      <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard label="Received" value={money(t.received)} tone="primary" icon={Wallet} testid="stat-received" />
        <StatCard label="Paid out / spent" value={money(t.made)} icon={ArrowsLeftRight} testid="stat-made" />
        <StatCard label="Settled payments" value={t.paid_count || 0} icon={Receipt} testid="stat-paid-count" />
        <StatCard label="Awaiting settlement" value={t.pending_count || 0} tone={t.pending_count ? "danger" : "default"} icon={Receipt} testid="stat-pending-count" />
      </div>

      <Tabs value={view} onValueChange={setView} className="mb-4 w-full overflow-x-auto">
        <TabsList data-testid="payments-tabs" className="w-max">
          <TabsTrigger value="activity" data-testid="tab-activity">Activity</TabsTrigger>
          <TabsTrigger value="requests" data-testid="tab-requests">Payment links</TabsTrigger>
          <TabsTrigger value="channels" data-testid="tab-channels">Payout channels</TabsTrigger>
          <TabsTrigger value="payouts" data-testid="tab-payouts">Payouts</TabsTrigger>
        </TabsList>
      </Tabs>

      {view === "activity" && (
        <>
          <div className="mb-4 flex gap-2">
            {["all", "made", "received"].map((d) => (
              <button
                key={d}
                data-testid={`direction-${d}`}
                onClick={() => setDirection(d)}
                className={`border px-3 py-2 text-xs font-semibold uppercase tracking-wider ${
                  direction === d ? "border-[#002FA7] bg-[#002FA7] text-white" : "border-border bg-white text-slate-600"
                }`}
              >
                {d}
              </button>
            ))}
          </div>
          {ledger.rows.length === 0 ? (
            <EmptyState title="No payments yet" description="Payments for orders, shipments and payment links land here." testid="ledger-empty" />
          ) : (
            <div className="border border-border bg-white" data-testid="ledger-list">
              <div className="md:hidden divide-y divide-border">
                {ledger.rows.map((r) => (
                  <div key={r.id} className="p-4" data-testid={`ledger-card-${r.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="truncate font-semibold">{r.reference || r.session_id?.slice(-8)}</div>                        <div className="overline text-[0.6rem] text-slate-400">{r.source.replace(/_/g, " ")} · {r.direction}</div>
                      </div>
                      <div className="text-right">
                        <div className="font-mono font-bold">{money(r.amount, r.currency)}</div>
                        <StatusBadge value={r.status === "paid" ? "approved" : "pending"} />
                      </div>
                    </div>
                    {r.status === "paid" && (
                      <Button size="sm" variant="outline" className="mt-2 w-full" data-testid={`receipt-${r.id}`} onClick={() => downloadReceipt(r)}>
                        <DownloadSimple size={14} className="mr-1" /> Receipt
                      </Button>
                    )}
                  </div>
                ))}
              </div>
              <table className="hidden w-full text-sm md:table">
                <thead className="border-b border-border bg-slate-50">
                  <tr className="overline text-left text-[0.65rem] text-slate-500">
                    <th className="px-4 py-3">Reference</th>
                    <th className="px-4 py-3">Source</th>
                    <th className="px-4 py-3">Counterparty</th>
                    <th className="px-4 py-3">Direction</th>
                    <th className="px-4 py-3">Date</th>
                    <th className="px-4 py-3">Amount</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3 text-right">Receipt</th>
                  </tr>
                </thead>
                <tbody>
                  {ledger.rows.map((r) => (
                    <tr key={r.id} className="border-b border-border last:border-0" data-testid={`ledger-row-${r.id}`}>
                      <td className="px-4 py-3 font-semibold">{r.reference || r.session_id?.slice(-8)}</td>
                      <td className="px-4 py-3 text-xs uppercase text-slate-500">{r.source.replace(/_/g, " ")}</td>
                      <td className="px-4 py-3 text-xs">{r.counterparty || "—"}</td>
                      <td className="px-4 py-3 text-xs uppercase">{r.direction}</td>
                      <td className="px-4 py-3 text-xs">{(r.created_at || "").slice(0, 16).replace("T", " ")}</td>
                      <td className="px-4 py-3 font-mono font-semibold">{money(r.amount, r.currency)}</td>
                      <td className="px-4 py-3"><StatusBadge value={r.status === "paid" ? "approved" : "pending"} /></td>
                      <td className="px-4 py-3 text-right">
                        {r.status === "paid" && (
                          <Button size="sm" variant="outline" data-testid={`receipt-${r.id}`} onClick={() => downloadReceipt(r)}>
                            <DownloadSimple size={14} className="mr-1" /> PDF
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {view === "requests" && (
        requests.length === 0 ? (
          <EmptyState title="No payment links yet" description="Create a link and anyone can pay it by card or wallet — no account needed." testid="requests-empty" />
        ) : (
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3" data-testid="requests-list">
            {requests.map((r) => (
              <div key={r.id} className="border border-border bg-white p-4" data-testid={`request-card-${r.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-display text-base font-semibold">{r.reference}</div>
                    <div className="truncate text-sm text-slate-600">{r.description}</div>
                  </div>
                  <StatusBadge value={r.status === "paid" ? "approved" : r.status === "cancelled" ? "closed" : "pending"} />
                </div>
                <div className="mt-2 font-mono text-lg font-bold text-[#002FA7]">{money(r.amount, r.currency)}</div>
                <div className="text-xs text-slate-500">
                  {r.payer_name || r.payer_email || "Anyone with the link"}{r.due_date ? ` · due ${r.due_date}` : ""}
                </div>
                <div className="mt-3 flex gap-2">
                  <Button size="sm" variant="outline" data-testid={`copy-link-${r.id}`} onClick={() => copyLink(r.token)}>
                    <Copy size={14} className="mr-1" /> Copy link
                  </Button>
                  <a href={`/pay/${r.token}`} target="_blank" rel="noreferrer">
                    <Button size="sm" variant="ghost" data-testid={`open-link-${r.id}`}>
                      <LinkSimple size={14} className="mr-1" /> Open
                    </Button>
                  </a>
                  {r.status === "open" && (
                    <Button size="sm" variant="ghost" data-testid={`cancel-request-${r.id}`} onClick={() => cancelRequest(r.id)}>
                      Cancel
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {view === "channels" && (
        channels.length === 0 ? (
          <EmptyState title="No payout channels yet" description="Carriers and sellers add a bank account, UPI ID or PayPal so they can be paid in their own currency." testid="channels-empty" />
        ) : (
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3" data-testid="channels-list">
            {channels.map((c) => (
              <div key={c.id} className="border border-border bg-white p-4" data-testid={`channel-card-${c.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="font-display text-base font-semibold">{c.label}</div>
                    <div className="overline text-[0.6rem] text-slate-500">
                      {c.method} · {c.currency.toUpperCase()} {c.country ? `· ${c.country}` : ""}
                    </div>
                  </div>
                  {c.is_default && <StatusBadge value="active" />}
                </div>
                <div className="mt-2 space-y-0.5 text-xs text-slate-600">
                  {c.owner_name && <div>{c.owner_name}</div>}
                  {c.method === "upi" && <div>UPI · {c.upi_id}</div>}
                  {c.method === "bank" && <div>{c.bank_name} · ****{String(c.account_number || "").slice(-4)}</div>}
                  {c.method === "paypal" && <div>{c.account_name}</div>}
                </div>
                <Button size="sm" variant="ghost" className="mt-3" data-testid={`delete-channel-${c.id}`} onClick={() => removeChannel(c.id)}>
                  <Trash size={14} />
                </Button>
              </div>
            ))}
          </div>
        )
      )}

      {view === "payouts" && (
        <div className="space-y-6">
          {isAdmin && (
            <div className="border border-border bg-white" data-testid="payout-summary">
              <div className="border-b border-border px-4 py-3 font-display text-sm font-bold">CARRIER EARNINGS</div>
              {summary.length === 0 ? (
                <div className="px-4 py-4 text-sm text-slate-500">No carrier earnings yet — earnings appear once a carrier accepts a shipment and it is paid.</div>
              ) : (
                <ul className="divide-y divide-border">
                  {summary.map((s) => (
                    <li key={`${s.carrier_id}-${s.currency}`} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3" data-testid={`summary-${s.carrier_id}`}>
                      <div>
                        <div className="font-semibold">{s.carrier_name}</div>
                        <div className="text-xs text-slate-500">
                          {s.shipments} shipment(s) · earned {money(s.earned, s.currency)} · paid out {money(s.paid_out, s.currency)}
                          {s.channels?.length ? ` · ${s.channels.map((c) => `${c.method}:${c.upi_id || c.bank_name || c.label}`).join(", ")}` : " · no payout channel on file"}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-mono font-bold text-[#002FA7]">{money(s.outstanding, s.currency)}</span>
                        <Button
                          size="sm"
                          data-testid={`pay-carrier-${s.carrier_id}`}
                          onClick={() => {
                            setPayoutForm({
                              payee_id: s.carrier_id, payee_name: s.carrier_name, amount: s.outstanding,
                              currency: s.currency, method: s.channels?.[0]?.method || "bank", reference: "", note: "",
                            });
                            setPayoutOpen(true);
                          }}
                        >
                          Record payout
                        </Button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          <div className="border border-border bg-white" data-testid="payouts-list">
            <div className="border-b border-border px-4 py-3 font-display text-sm font-bold">PAYOUT HISTORY</div>
            {payouts.length === 0 ? (
              <div className="px-4 py-4 text-sm text-slate-500">No payouts recorded yet.</div>
            ) : (
              <ul className="divide-y divide-border">
                {payouts.map((p) => (
                  <li key={p.id} className="flex items-center justify-between gap-3 px-4 py-3 text-sm" data-testid={`payout-${p.id}`}>
                    <div>
                      <div className="font-semibold">{p.payee_name}</div>
                      <div className="text-xs text-slate-500">
                        {p.method} · {(p.paid_at || "").slice(0, 16).replace("T", " ")}{p.reference ? ` · ${p.reference}` : ""}
                      </div>
                    </div>
                    <span className="font-mono font-bold">{money(p.amount, p.currency)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {/* Request payment dialog */}
      <Dialog open={reqOpen} onOpenChange={setReqOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md" data-testid="request-dialog">
          <DialogHeader>
            <DialogTitle>Request a payment</DialogTitle>
            <DialogDescription>Creates a shareable link anyone can pay by card or wallet — they don't need an account.</DialogDescription>
          </DialogHeader>
          <form onSubmit={createRequest} className="space-y-3">
            <div>
              <Label>What is it for</Label>
              <Input data-testid="request-description-input" required value={reqForm.description} onChange={(e) => setReqForm({ ...reqForm, description: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Amount</Label>
                <Input data-testid="request-amount-input" type="number" min="0.01" step="0.01" required value={reqForm.amount} onChange={(e) => setReqForm({ ...reqForm, amount: e.target.value })} />
              </div>
              <div>
                <Label>Currency</Label>
                <Select value={reqForm.currency} onValueChange={(v) => setReqForm({ ...reqForm, currency: v })}>
                  <SelectTrigger data-testid="request-currency-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {currencyOptions.map((c) => <SelectItem key={c} value={c}>{c.toUpperCase()}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Payer name</Label>
                <Input data-testid="request-payer-name-input" value={reqForm.payer_name} onChange={(e) => setReqForm({ ...reqForm, payer_name: e.target.value })} />
              </div>
              <div>
                <Label>Payer email</Label>
                <Input data-testid="request-payer-email-input" type="email" value={reqForm.payer_email} onChange={(e) => setReqForm({ ...reqForm, payer_email: e.target.value })} />
              </div>
              <div className="col-span-2">
                <Label>Due date</Label>
                <Input data-testid="request-due-input" type="date" value={reqForm.due_date} onChange={(e) => setReqForm({ ...reqForm, due_date: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>Note</Label>
              <Textarea data-testid="request-note-input" rows={2} value={reqForm.note} onChange={(e) => setReqForm({ ...reqForm, note: e.target.value })} />
            </div>
            <DialogFooter>
              <Button type="submit" data-testid="submit-request-button" className="w-full sm:w-auto">Create link</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Payout channel dialog */}
      <Dialog open={chanOpen} onOpenChange={setChanOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md" data-testid="channel-dialog">
          <DialogHeader>
            <DialogTitle>Add a payout channel</DialogTitle>
            <DialogDescription>Where should money reach you? Bank transfer, UPI, PayPal or cash — in any supported currency.</DialogDescription>
          </DialogHeader>
          <form onSubmit={saveChannel} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label>Label</Label>
                <Input data-testid="channel-label-input" required value={chanForm.label} onChange={(e) => setChanForm({ ...chanForm, label: e.target.value })} placeholder="Main business account" />
              </div>
              <div>
                <Label>Method</Label>
                <Select value={chanForm.method} onValueChange={(v) => setChanForm({ ...chanForm, method: v })}>
                  <SelectTrigger data-testid="channel-method-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bank">Bank transfer</SelectItem>
                    <SelectItem value="upi">UPI</SelectItem>
                    <SelectItem value="paypal">PayPal</SelectItem>
                    <SelectItem value="cash">Cash</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Currency</Label>
                <Select value={chanForm.currency} onValueChange={(v) => setChanForm({ ...chanForm, currency: v })}>
                  <SelectTrigger data-testid="channel-currency-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {currencyOptions.map((c) => <SelectItem key={c} value={c}>{c.toUpperCase()}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {chanForm.method === "upi" && (
                <div className="col-span-2">
                  <Label>UPI ID</Label>
                  <Input data-testid="channel-upi-input" value={chanForm.upi_id} onChange={(e) => setChanForm({ ...chanForm, upi_id: e.target.value })} placeholder="carrier@okbank" />
                </div>
              )}
              {chanForm.method === "bank" && (
                <>
                  <div>
                    <Label>Account name</Label>
                    <Input data-testid="channel-account-name-input" value={chanForm.account_name} onChange={(e) => setChanForm({ ...chanForm, account_name: e.target.value })} />
                  </div>
                  <div>
                    <Label>Account number</Label>
                    <Input data-testid="channel-account-number-input" value={chanForm.account_number} onChange={(e) => setChanForm({ ...chanForm, account_number: e.target.value })} />
                  </div>
                  <div>
                    <Label>Bank</Label>
                    <Input data-testid="channel-bank-input" value={chanForm.bank_name} onChange={(e) => setChanForm({ ...chanForm, bank_name: e.target.value })} />
                  </div>
                  <div>
                    <Label>SWIFT / IBAN</Label>
                    <Input data-testid="channel-swift-input" value={chanForm.swift_iban} onChange={(e) => setChanForm({ ...chanForm, swift_iban: e.target.value })} />
                  </div>
                </>
              )}
              {chanForm.method === "paypal" && (
                <div className="col-span-2">
                  <Label>PayPal email</Label>
                  <Input data-testid="channel-paypal-input" value={chanForm.account_name} onChange={(e) => setChanForm({ ...chanForm, account_name: e.target.value })} />
                </div>
              )}
              <div className="col-span-2">
                <Label>Country</Label>
                <Select value={chanForm.country || "none"} onValueChange={(v) => setChanForm({ ...chanForm, country: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="channel-country-input"><SelectValue placeholder="Select country" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="none">Not set</SelectItem>
                    {COUNTRIES.map(([code, name]) => (
                      <SelectItem key={code} value={code}>{code} — {name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button type="submit" data-testid="submit-channel-button" className="w-full sm:w-auto">Save channel</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Record payout dialog */}
      <Dialog open={payoutOpen} onOpenChange={setPayoutOpen}>
        <DialogContent className="sm:max-w-md" data-testid="payout-dialog">
          <DialogHeader>
            <DialogTitle>Record a payout</DialogTitle>
            <DialogDescription>Log money sent to {payoutForm?.payee_name} through their own payout channel.</DialogDescription>
          </DialogHeader>
          {payoutForm && (
            <form onSubmit={recordPayout} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Amount</Label>
                  <Input data-testid="payout-amount-input" type="number" min="0.01" step="0.01" required value={payoutForm.amount} onChange={(e) => setPayoutForm({ ...payoutForm, amount: e.target.value })} />
                </div>
                <div>
                  <Label>Method</Label>
                  <Select value={payoutForm.method} onValueChange={(v) => setPayoutForm({ ...payoutForm, method: v })}>
                    <SelectTrigger data-testid="payout-method-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bank">Bank transfer</SelectItem>
                      <SelectItem value="upi">UPI</SelectItem>
                      <SelectItem value="paypal">PayPal</SelectItem>
                      <SelectItem value="cash">Cash</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label>Reference</Label>
                  <Input data-testid="payout-reference-input" value={payoutForm.reference} onChange={(e) => setPayoutForm({ ...payoutForm, reference: e.target.value })} placeholder="Bank/UPI transaction id" />
                </div>
              </div>
              <DialogFooter>
                <Button type="submit" data-testid="submit-payout-button" className="w-full sm:w-auto">Record payout</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
